'use client';

import { Canvas, useFrame } from '@react-three/fiber';
import { useRef, useMemo, Suspense, useEffect } from 'react';
import * as THREE from 'three';

const RisingParticles = ({ size = 0.12, count = 120 }: { size?: number, count?: number }) => {
    const streamRef = useRef<any>(null);

    // Stream Data (Upward Spiral)
    const streamData = useMemo(() => {
        // count is now dynamic from prop
        const pos = new Float32Array(count * 3);
        const speeds = new Float32Array(count);
        const sizes = new Float32Array(count);

        for (let i = 0; i < count; i++) {
            // Spread widely across x and z
            const x = (Math.random() - 0.5) * 25;
            const z = (Math.random() - 0.5) * 10;
            const y = (Math.random() - 0.5) * 40; // Even Wider Spread (-20 to 20)

            pos[i * 3] = x;
            pos[i * 3 + 1] = y;
            pos[i * 3 + 2] = z;

            speeds[i] = 0.008 + Math.random() * 0.015; // Increased speed
            sizes[i] = Math.random();
        }

        return { pos, speeds, sizes };
    }, [count]);

    // Texture for Round Dots
    const circleTexture = useMemo(() => {
        if (typeof window === 'undefined') return null;
        const canvas = document.createElement('canvas');
        canvas.width = 32;
        canvas.height = 32;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.beginPath();
            ctx.arc(16, 16, 14, 0, 2 * Math.PI);
            ctx.fillStyle = 'white';
            ctx.fill();
        }
        const texture = new THREE.CanvasTexture(canvas);
        return texture;
    }, []);

    // Cleanup
    useFrame(() => {
        // No per-frame cleanup needed, but good to have hook available
    });

    useEffect(() => {
        return () => {
            circleTexture?.dispose();
        };
    }, [circleTexture]);

    useFrame((state) => {
        if (streamRef.current) {
            const positions = streamRef.current.geometry.attributes.position.array as Float32Array;
            for (let i = 0; i < count; i++) {
                const yIdx = i * 3 + 1;
                positions[yIdx] += streamData.speeds[i];

                if (positions[yIdx] > 20) {
                    positions[yIdx] = -20;
                }
            }
            streamRef.current.geometry.attributes.position.needsUpdate = true;
        }
    });

    return (
        <points ref={streamRef}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={streamData.pos.length / 3}
                    array={streamData.pos}
                    itemSize={3}
                    args={[streamData.pos, 3]}
                />
                <bufferAttribute
                    attach="attributes-size"
                    count={streamData.sizes.length}
                    array={streamData.sizes}
                    itemSize={1}
                    args={[streamData.sizes, 1]}
                />
            </bufferGeometry>
            <pointsMaterial
                size={size} // Use prop
                color="#3730a3" // Deeper Indigo for high contrast
                transparent={true}
                opacity={0.8} // High visibility
                sizeAttenuation={true}
                map={circleTexture}
                alphaMap={circleTexture}
                alphaTest={0.5}
                depthWrite={false}
            />
        </points>
    );
};

export default function RisingParticlesScene({ size, count }: { size?: number, count?: number }) {
    return (
        <div className="absolute inset-0 w-full h-full pointer-events-none">
            <Canvas
                camera={{ position: [0, 0, 15], fov: 45 }}
                gl={{ antialias: true, alpha: true, powerPreference: 'default' }}
                dpr={[1, 1.5]} // Limit DPR to save memory
            >
                <Suspense fallback={null}>
                    <RisingParticles size={size} count={count} />
                </Suspense>
            </Canvas>
        </div>
    );
}
